// Amos-83 Request Portal — browser → https://fh-portal.canopymsp.com/api/portal/* with the
// requester-pool ID token. Never touches the staff API Gateway (different pool, different host).
import { fetchAuthSession } from 'aws-amplify/auth';
import { PORTAL_API_URL } from '../config/auth';

export class PortalApiError extends Error {
  status: number;
  detail: any;
  constructor(message: string, status: number, detail: any) {
    super(message);
    this.name = 'PortalApiError';
    this.status = status;
    this.detail = detail;
  }
}

export interface Term { term: string; effective: string; expiration: string }
// last_requested_at (Amos-104): newest earlier request of this policy by the same requester, for the repeat warning
export interface Validated { ref: string; insured_name: string; terms: Term[]; last_requested_at?: string | null }
export interface ValidateResult { validated: Validated[]; unvalidated: string[]; rejected_shape: string[]; file?: string }
export interface Created { request_id: string; ref: string; insured_name: string }
export interface CreateResult extends ValidateResult {
  submission_id: string; replayed: boolean; created: Created[]; failed: Array<{ ref: string; error: string }>;
  audit_failed?: string[]; source?: string;
}
export interface Me { email: string; broker_group: string; test_mode: boolean; backstop_cc: string; max_refs: number; upload_types: string[] }
export interface StatusRow {
  request_id?: string; ref: string; insured_name?: string; submission_id?: string;
  submitted_at: string; status: string; status_at: string; source?: string; reason?: string;
  sent_at?: string | null; sent_to?: string[];   // Amos-104 §d: "Sent <date> to <To>"
}
// not_found is always [] since Amos-104 (Not-found is shown once, on the submit confirmation)
export interface ListResult { items: StatusRow[]; not_found?: StatusRow[]; next_cursor: string | null }

async function token(): Promise<string> {
  try {
    const s = await fetchAuthSession();
    const t = s.tokens?.idToken?.toString();
    if (!t) throw new Error('no token');
    return t;
  } catch {
    throw new PortalApiError('session', 401, null);
  }
}

async function call(path: string, init: RequestInit = {}): Promise<any> {
  const t = await token();
  let resp: Response;
  try {
    resp = await fetch(`${PORTAL_API_URL}${path}`, {
      ...init,
      headers: { Authorization: `Bearer ${t}`, ...(init.headers || {}) },
    });
  } catch {
    throw new PortalApiError('network', 0, null);
  }
  if (!resp.ok) {
    const body = await resp.json().catch(() => ({}));
    const detail = body.detail ?? body.error ?? `HTTP ${resp.status}`;
    throw new PortalApiError(typeof detail === 'string' ? detail : JSON.stringify(detail), resp.status, detail);
  }
  return resp.json();
}

const json = (body: unknown): RequestInit => ({
  method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
});

function multipart(file: File, fields: Record<string, string | undefined>): RequestInit {
  const fd = new FormData();
  fd.append('list', file, file.name);
  for (const [k, v] of Object.entries(fields)) if (v !== undefined && v !== '') fd.append(k, v);
  return { method: 'POST', body: fd };
}

export const portalApi = {
  me: (): Promise<Me> => call('/api/portal/me'),
  validate: (body: { refs?: string[]; text?: string }): Promise<ValidateResult> => call('/api/portal/validate', json(body)),
  validateUpload: (file: File): Promise<ValidateResult> => call('/api/portal/validate/upload', multipart(file, {})),
  create: (body: {
    submission_id: string; refs?: string[]; text?: string;
    period: 'all'; note?: string; deliver_to?: string[];   // Amos-104 G2: always all periods
  }): Promise<CreateResult> => call('/api/portal/requests', json(body)),
  createUpload: (file: File, fields: { submission_id: string; note?: string; deliver_to?: string }): Promise<CreateResult> =>
    call('/api/portal/requests/upload', multipart(file, fields)),
  list: (cursor?: string | null, limit = 100): Promise<ListResult> =>
    call(`/api/portal/requests?limit=${limit}${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`),
};

export function newSubmissionId(): string {
  // 8–64 chars [A-Za-z0-9_-]; crypto UUID when available
  const c: any = (typeof crypto !== 'undefined' && crypto) || {};
  const raw = typeof c.randomUUID === 'function' ? c.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `web-${raw}`.replace(/[^A-Za-z0-9_-]/g, '').slice(0, 64);
}
